import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, SelectField, AreaField } from "@/components/common/Field";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { NewMatterDialog } from "@/components/shell/NewMatterDialog";
import { AIIntakeDialog } from "@/components/shell/AIIntakeDialog";
import { Sparkles, UploadCloud, FileText } from "lucide-react";
import { FinanceForm } from "@/components/finance/FinanceForm";
import { ACCEPT, MAX_FILE_BYTES, humanSize, kindOf } from "@/lib/upload";

const DOC_CATEGORIES = ["Pleadings", "Affidavits", "Orders", "Evidence", "Notices", "Correspondence", "Research", "Drafts", "Court filings", "Exhibits", "Media", "Miscellaneous"];

// A single controlled dialog host for every quick-create except Matter (which is multi-step).
export function QuickAddDialogs({ type, onClose, context = {} }) {
  const { db, create, pushNotification, uploadDocument, ai, scope } = useApp();
  const [form, setForm] = useState({ priority: "Medium", status: "To Do", ...seedFor(type, context) });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [suggest, setSuggest] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Jev: debounced urgency suggestion for tasks.
  useEffect(() => {
    if (type !== "task" || !form.title || form.title.trim().length < 6) { setSuggest(null); return; }
    const t = setTimeout(async () => {
      const dueInDays = form.dueDate ? Math.ceil((new Date(form.dueDate) - Date.now()) / 86400000) : null;
      const r = await ai.scoreTask({ title: form.title, description: form.description || "", dueInDays });
      setSuggest(r?.ai ? r : null);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [type, form.title, form.description, form.dueDate]);

  const pickFile = async (file) => {
    if (!file) return;
    set("file", file);
    if (!form.name) set("name", file.name);
    const r = await ai.classifyDocument({ filename: file.name });
    if (r?.ai && r.category) { set("category", r.category); set("aiCategory", r.category); set("aiConfidence", r.confidence); }
  };

  const matterOpts = useMemo(() => db.matters.map((m) => ({ value: m.id, label: `${m.caseNumber} · ${m.title}` })), [db.matters]);
  const clientOpts = useMemo(() => db.clients.map((c) => ({ value: c.id, label: c.name })), [db.clients]);
  const userOpts = useMemo(() => db.users.map((u) => ({ value: u.id, label: u.name })), [db.users]);

  if (type === "billing") {
    return <FinanceForm kind="invoices" matterId={context.matterId} onClose={onClose} onSaved={async () => toast.success("Payment request issued.")} />;
  }

  if (type === "ai-intake") {
    return <AIIntakeDialog open onOpenChange={(o) => !o && onClose()} />;
  }
  if (type === "matter") {
    return <NewMatterDialog open onOpenChange={(o) => !o && onClose()} defaultClientId={context.clientId} />;
  }

  const CONFIG = {
    client: {
      title: "New Client", testid: "new-client-dialog",
      validate: () => (!form.name?.trim() ? { name: "Name is required" } : {}),
      submit: () => {
        const rec = create("clients", {
          name: form.name, type: form.type || "Individual", mobile: form.mobile || "—",
          email: form.email || "—", status: "Active", outstanding: 0, rm: "u_satish",
          address: form.address || "—",
        });
        pushNotification({ category: "System", title: "Client added", body: `${form.name} was added.` });
        toast.success("Client created.");
        return rec;
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Client Name" required value={form.name || ""} onChange={(v) => set("name", v)} error={err.name} className="col-span-2" testid="client-name-input" />
          <SelectField label="Client Type" value={form.type || "Individual"} onChange={(v) => set("type", v)} options={["Individual", "Company", "Partnership", "LLP", "Trust", "Society", "Government body", "NGO", "Institution", "Other"]} />
          <TextField label="Mobile" value={form.mobile || ""} onChange={(v) => set("mobile", v)} />
          <TextField label="Email" value={form.email || ""} onChange={(v) => set("email", v)} />
          <TextField label="Address" value={form.address || ""} onChange={(v) => set("address", v)} />
        </div>
      ),
    },
    task: {
      title: "New Task", testid: "new-task-dialog",
      validate: () => { const e = {}; if (!form.title?.trim()) e.title = "Title is required"; if (!form.matterId) e.matterId = "Select a matter"; return e; },
      submit: () => {
        const rec = create("tasks", {
          title: form.title, matterId: form.matterId, assignee: form.assignee || "u_kesav",
          priority: form.priority, status: form.status || "To Do", dueDate: form.dueDate || null,
          category: form.category || "General", description: form.description || "",
          aiPriority: suggest?.priority || null, limitationCritical: !!suggest?.limitationCritical,
        });
        const who = db.users.find((u) => u.id === (form.assignee || "u_kesav"))?.name;
        toast.success(`Task assigned to ${who?.split(" ")[0] || "team"}.`);
        return rec;
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Task Title" required value={form.title || ""} onChange={(v) => set("title", v)} error={err.title} className="col-span-2" testid="task-title-input" />
          {suggest && (
            <button type="button" onClick={() => { set("priority", suggest.priority); toast.success(`Priority set to ${suggest.priority}.`); }} className="col-span-2 flex items-center gap-2 rounded-lg border border-copper/40 bg-copper-light px-3 py-2 text-left text-xs text-ink" data-testid="task-ai-suggestion">
              <Sparkles className="h-3.5 w-3.5 text-copper" />
              <span>Jev suggests <b>{suggest.priority}</b> priority{suggest.limitationCritical && <span className="ml-1 rounded bg-danger/10 px-1.5 py-0.5 font-medium text-danger">Limitation-critical</span>}</span>
              <span className="ml-auto text-copper">Apply</span>
            </button>
          )}
          <SelectField label="Matter" required value={form.matterId || ""} onChange={(v) => set("matterId", v)} options={matterOpts} error={err.matterId} className="col-span-2" testid="task-matter-select" />
          <SelectField label="Assignee" value={form.assignee || "u_kesav"} onChange={(v) => set("assignee", v)} options={userOpts} />
          <SelectField label="Priority" value={form.priority} onChange={(v) => set("priority", v)} options={["High", "Medium", "Low"]} />
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={["To Do", "In Progress", "Review", "Completed"]} />
          <TextField label="Due Date" type="date" value={form.dueDate || ""} onChange={(v) => set("dueDate", v)} />
          <AreaField label="Description" value={form.description || ""} onChange={(v) => set("description", v)} className="col-span-2" />
        </div>
      ),
    },
    hearing: {
      title: "Add Hearing", testid: "new-hearing-dialog",
      validate: () => { const e = {}; if (!form.matterId) e.matterId = "Select a matter"; if (!form.date) e.date = "Date is required"; return e; },
      submit: () => {
        const rec = create("hearings", {
          matterId: form.matterId, date: form.date, court: form.court || "—", judge: form.judge || "—",
          purpose: form.purpose || "Hearing", outcome: null, order: null, nextDate: null,
          attendance: form.attendance || "—", notes: form.notes || "",
        });
        pushNotification({ category: "Hearings", title: "Hearing scheduled", body: `${form.purpose || "Hearing"} added.`, matterId: form.matterId });
        toast.success("Hearing added.");
        return rec;
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Matter" required value={form.matterId || ""} onChange={(v) => set("matterId", v)} options={matterOpts} error={err.matterId} className="col-span-2" testid="hearing-matter-select" />
          <TextField label="Date & Time" type="datetime-local" required value={form.date || ""} onChange={(v) => set("date", v)} error={err.date} testid="hearing-date-input" />
          <TextField label="Purpose" value={form.purpose || ""} onChange={(v) => set("purpose", v)} placeholder="e.g. Admission" />
          <TextField label="Court" value={form.court || ""} onChange={(v) => set("court", v)} />
          <TextField label="Judge / Bench" value={form.judge || ""} onChange={(v) => set("judge", v)} />
          <AreaField label="Notes" value={form.notes || ""} onChange={(v) => set("notes", v)} className="col-span-2" />
        </div>
      ),
    },
    document: {
      title: "Upload Document", testid: "new-document-dialog",
      validate: () => { const e = {}; if (!form.file) e.file = "Choose a file to upload"; if (!form.matterId) e.matterId = "Select a matter"; return e; },
      submit: async () => {
        setBusy(true);
        try {
          const rec = await uploadDocument({ file: form.file, matterId: form.matterId, category: form.categoryManual ? form.category : undefined, onProgress: setProgress });
          pushNotification({ category: "Documents", title: "Document uploaded", body: `${rec.name} added.`, matterId: form.matterId });
          toast.success(`${rec.name} uploaded${rec.aiClassified ? ` · filed under ${rec.category}` : ""}.`);
          return rec;
        } catch (e) {
          toast.error(e?.response?.data?.detail || e?.message || "Upload failed. Please try again.");
          throw e;
        } finally { setBusy(false); }
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <input ref={fileRef} type="file" className="hidden" accept={ACCEPT} onChange={(e) => pickFile(e.target.files?.[0])} data-testid="document-file-input" />
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
            className={`col-span-2 cursor-pointer rounded-lg border border-dashed p-5 text-center text-sm transition-colors ${err.file ? "border-danger bg-danger/5" : "border-line-strong bg-surface hover:border-copper/60"}`}
            data-testid="document-dropzone"
          >
            {form.file ? (
              <div className="flex items-center justify-center gap-2 text-ink"><FileText className="h-5 w-5 text-copper" /><span className="font-medium">{form.file.name}</span><span className="text-xs text-ink-muted">{humanSize(form.file.size)} · {kindOf(form.file)}</span></div>
            ) : (
              <div className="text-ink-2"><UploadCloud className="mx-auto mb-1 h-6 w-6 text-copper" />Drag & drop a PDF, exhibit, photo, audio or video here, or <span className="text-copper underline">browse</span><div className="mt-1 text-xs text-ink-muted">PDF, images, DOCX, MP4, MP3 · up to {humanSize(MAX_FILE_BYTES)} (chunked upload)</div></div>
            )}
            {err.file && <div className="mt-1 text-xs text-danger">{err.file}</div>}
          </div>
          {busy && <div className="col-span-2 h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full bg-copper transition-[width]" style={{ width: `${progress}%` }} /></div>}
          <SelectField label="Matter" required value={form.matterId || ""} onChange={(v) => set("matterId", v)} options={matterOpts} error={err.matterId} testid="document-matter-select" />
          <div>
            <SelectField label="Category" value={form.category || "Pleadings"} onChange={(v) => { set("category", v); set("categoryManual", true); }} options={DOC_CATEGORIES} testid="document-category-select" />
            {form.aiCategory && <div className="mt-1 flex items-center gap-1 text-xs text-copper" data-testid="document-ai-category"><Sparkles className="h-3 w-3" /> Jev filed this as {form.aiCategory}</div>}
          </div>
        </div>
      ),
    },
    draft: {
      title: "Create Draft", testid: "new-draft-dialog",
      validate: () => { const e = {}; if (!form.title?.trim()) e.title = "Title required"; if (!form.matterId) e.matterId = "Select a matter"; return e; },
      submit: () => {
        const rec = create("drafts", {
          title: form.title, matterId: form.matterId, type: form.type || "Writ Petition",
          status: "Draft", version: 1, updatedAt: new Date().toISOString(), updatedBy: "u_satish",
        });
        toast.success("Draft created.");
        return rec;
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Draft Title" required value={form.title || ""} onChange={(v) => set("title", v)} error={err.title} className="col-span-2" testid="draft-title-input" />
          <SelectField label="Matter" required value={form.matterId || ""} onChange={(v) => set("matterId", v)} options={matterOpts} error={err.matterId} testid="draft-matter-select" />
          <SelectField label="Document Type" value={form.type || "Writ Petition"} onChange={(v) => set("type", v)} options={["Writ Petition", "Counter", "Affidavit", "IA", "Appeal", "Written Statement", "Legal Notice", "Legal Opinion", "Arbitration"]} />
        </div>
      ),
    },
  };

  const cfg = CONFIG[type];
  if (!cfg) return null;

  const handleSubmit = async () => {
    const e = cfg.validate();
    setErr(e);
    if (Object.keys(e).length) return;
    try { await cfg.submit(); } catch { return; }
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid={cfg.testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{cfg.title}</DialogTitle>
        </DialogHeader>
        <div className="nyk-scroll max-h-[60vh] overflow-y-auto py-1 pr-1">{cfg.body}</div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} data-testid="quickadd-submit-btn">{busy ? `Uploading… ${progress}%` : type === "document" ? "Upload" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function seedFor(type, context) {
  if (type === "task" || type === "hearing" || type === "document" || type === "draft" || type === "billing") {
    return { matterId: context.matterId || "" };
  }
  return {};
}
